import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_URL, STORAGE_KEYS } from '@/config/app.config';
import type { ApiResponse } from '@cloudflare-atmb/shared';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
    baseURL: API_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Add auth token if exists
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

// Response interceptor
apiClient.interceptors.response.use(
    response => {
        return response.data;
    },
    (error: AxiosError<ApiResponse>) => {
        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
            const currentPath = window.location.pathname;
            const hasToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

            // 清除 token
            localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER_INFO);

            // 只在已登录状态（有token）且不在登录/注册页时才跳转
            if (hasToken && !['/login', '/register'].includes(currentPath)) {
                window.location.href = '/login';
            }
        }

        // Format error message
        let errorMessage = 'Unknown error';

        const errorData = error.response?.data?.error;
        if (errorData) {
            // Handle Zod validation errors
            if (typeof errorData === 'object' && 'issues' in errorData && Array.isArray(errorData.issues)) {
                const firstIssue = errorData.issues[0];
                errorMessage = firstIssue.message || 'Validation failed';
                // Optional: Append field path if useful
                // if (firstIssue.path) errorMessage += ` (${firstIssue.path.join('.')})`;
            } else if (typeof errorData === 'object' && 'message' in errorData) {
                // Handle standard API errors
                errorMessage = (errorData as any).message;
            } else if (typeof errorData === 'string') {
                errorMessage = errorData;
            }
        }

        if (errorMessage === 'Unknown error') {
            errorMessage = error.message || 'Request failed';
        }

        return Promise.reject(new Error(errorMessage));
    }
);

export default apiClient;
